import type { UseQueryResult } from '@tanstack/react-query';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CHART_RANGE_MINUTES, TIME_FRAMES } from '@/components/Charts/utils';

import { NATIVE_TOKEN_DATA_ADDRESSES } from '@/constants/addresses';
import { isIssuerSupported, issuerDefinitions } from '@/constants/issuers';
import { REFRESH_INTERVAL } from '@/constants/rpc';

import type {
  TimeSeriesAreaData,
  TimeSeriesCandlestickData,
} from '@/ui/Charts/TimeSeriesChart/types';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import type { GetTimeSeriesResponse } from '@/utils/defined.types';
import { fetcher } from '@/utils/fetcher';
import { isTokenResultNativeAsset } from '@/utils/multichain';
import { routes } from '@/utils/routes';
import { objectToQueryString } from '@/utils/string';

export type TokenPriceSeriesOptions = {
  chartRangeInMinutes?: number;
  resolution?: string;
  fromTimestamp?: number;
  toTimestamp?: number;
  historicalToTimestamps?: number[];
  autoRefresh?: boolean;
  /** Poll cadence while `autoRefresh` is on. Defaults to the shared `REFRESH_INTERVAL`. */
  refreshIntervalMs?: number;
};

export type TokenPriceSeriesData = TimeSeriesAreaData & TimeSeriesCandlestickData;

type TokenPriceSeriesParams = Required<
  Pick<TokenPriceSeriesOptions, 'chartRangeInMinutes' | 'resolution'>
> & {
  symbol: string;
  fromTimestamp?: number;
  toTimestamp?: number;
  source?: 'coingecko';
};

/** Match the endpoint's 10-second cache buckets when the series is fetch-once. */
const STATIC_WINDOW_BUCKET_MS = 10_000;

export const tokenPriceSeriesQueryKey = (params: TokenPriceSeriesParams | undefined) =>
  ['token-price-series', params] as const;

const fetchTokenPriceSeries = (
  {
    symbol,
    resolution,
    chartRangeInMinutes,
    fromTimestamp,
    toTimestamp,
    source,
  }: TokenPriceSeriesParams,
  windowBucketMs: number,
) => {
  // Snap the rolling window to the bucket so refetches inside one bucket hit the
  // endpoint's cache instead of producing a new from/to every time.
  const to = Math.floor((toTimestamp ?? Date.now()) / windowBucketMs) * (windowBucketMs / 1_000);
  const from = fromTimestamp ?? to - chartRangeInMinutes * 60;

  const processedResolution = resolution === TIME_FRAMES.WEEK ? '7D' : resolution;
  const params = { symbol, resolution: processedResolution, from, to, source };
  const url = `${routes.api.CHART_HISTORY}${objectToQueryString(params)}`;

  return fetcher<GetTimeSeriesResponse>(url);
};

const combineHistoricalQueries = (
  results: Array<UseQueryResult<GetTimeSeriesResponse, Error>>,
) => ({
  timeSeries: results.map(({ data }) => data),
  isFetching: results.some(({ isFetching }) => isFetching),
  hasMoreData: results.every(({ data, isError }) => !isError && data?.s !== 'no_data'),
});

/** Converts aligned OHLCV arrays into points shared by area and candlestick charts. */
export const toTokenPriceSeriesData = (
  timeSeries: GetTimeSeriesResponse | undefined,
): TokenPriceSeriesData[] => {
  if (!timeSeries) return [];

  const { o: opens, h: highs, l: lows, c: closes, t: timestamps, volume } = timeSeries;
  const completedCandleCount = Math.min(
    opens.length,
    highs.length,
    lows.length,
    closes.length,
    timestamps.length,
  );
  const points: TokenPriceSeriesData[] = [];

  for (let index = 0; index < completedCandleCount; index++) {
    const barValue = Number(volume[index]);

    points.push({
      timestamp: timestamps[index] * 1000,
      value: closes[index],
      open: opens[index],
      high: highs[index],
      low: lows[index],
      close: closes[index],
      ...(isFinite(barValue) ? { barValue } : undefined),
    });
  }

  return points;
};

/**
 * Re-denominates a USD series in units of a quote token by dividing candles that share a
 * timestamp. Highs divide by the quote's low (and vice versa) to bound the ratio; candles with
 * a zero or non-finite quote leg are dropped, and volume is omitted since it has no pair meaning.
 */
export const toPairSeriesData = (
  base: TokenPriceSeriesData[],
  quote: TokenPriceSeriesData[],
): TokenPriceSeriesData[] => {
  const quoteByTimestamp = new Map(quote.map((point) => [point.timestamp, point]));
  const points: TokenPriceSeriesData[] = [];

  for (const { timestamp, open, high, low, close } of base) {
    const quotePoint = quoteByTimestamp.get(timestamp);
    if (!quotePoint) continue;

    const pairOpen = open / quotePoint.open;
    const pairHigh = high / quotePoint.low;
    const pairLow = low / quotePoint.high;
    const pairClose = close / quotePoint.close;
    if (![pairOpen, pairHigh, pairLow, pairClose].every((v) => Number.isFinite(v) && v > 0)) {
      continue;
    }

    points.push({
      timestamp,
      value: pairClose,
      open: pairOpen,
      high: pairHigh,
      low: pairLow,
      close: pairClose,
    });
  }

  return points;
};

export const useTokenPriceSeries = (
  token: TokenResult | undefined,
  options: TokenPriceSeriesOptions = {},
) => {
  const {
    chartRangeInMinutes = CHART_RANGE_MINUTES.DAILY,
    resolution = TIME_FRAMES.ONE_HOUR,
    fromTimestamp,
    toTimestamp,
    historicalToTimestamps = [],
    autoRefresh = false,
    refreshIntervalMs = REFRESH_INTERVAL,
  } = options;

  const tokenAddress =
    token && isTokenResultNativeAsset(token)
      ? NATIVE_TOKEN_DATA_ADDRESSES[token.chainId]
      : token?.address;

  const chainId = token?.chainId;
  const issuerName = token?.issuer?.name;

  const params = useMemo<TokenPriceSeriesParams | undefined>(() => {
    if (!tokenAddress || chainId == null) return undefined;

    const source = isIssuerSupported(issuerName)
      ? issuerDefinitions[issuerName].priceSource
      : undefined;
    const symbol = `${tokenAddress}:${chainId}`;

    return { symbol, resolution, chartRangeInMinutes, fromTimestamp, toTimestamp, source };
  }, [
    chainId,
    chartRangeInMinutes,
    fromTimestamp,
    issuerName,
    resolution,
    toTimestamp,
    tokenAddress,
  ]);

  // Polling is opt-in. When on, the window bucket follows the interval so every
  // poll lands on a fresh window; otherwise snap to the static cache bucket.
  const refetchInterval = autoRefresh ? refreshIntervalMs : false;
  const windowBucketMs = refetchInterval === false ? STATIC_WINDOW_BUCKET_MS : refetchInterval;

  const { data: timeSeries, ...query } = useQuery<GetTimeSeriesResponse, Error>({
    queryKey: tokenPriceSeriesQueryKey(params),
    queryFn: () => fetchTokenPriceSeries(params!, windowBucketMs),
    enabled: params != null,
    refetchInterval,
    retry: false,
  });

  const historicalParams = useMemo<TokenPriceSeriesParams[]>(() => {
    if (!params) return [];
    return historicalToTimestamps.map((timestamp) => ({ ...params, toTimestamp: timestamp }));
  }, [historicalToTimestamps, params]);

  const {
    timeSeries: historicalTimeSeries,
    isFetching: isFetchingHistorical,
    hasMoreData: hasMoreHistoricalData,
  } = useQueries({
    queries: historicalParams.map((historicalParam) => ({
      queryKey: tokenPriceSeriesQueryKey(historicalParam),
      queryFn: () => fetchTokenPriceSeries(historicalParam, STATIC_WINDOW_BUCKET_MS),
      enabled: params != null,
      retry: 2,
      staleTime: Infinity,
    })),
    combine: combineHistoricalQueries,
  });

  const data = useMemo(() => {
    const pointsByTimestamp = new Map<number, TokenPriceSeriesData>();

    for (const response of [...historicalTimeSeries, timeSeries]) {
      for (const point of toTokenPriceSeriesData(response)) {
        pointsByTimestamp.set(point.timestamp, point);
      }
    }

    return Array.from(pointsByTimestamp.values()).toSorted(
      (pointA, pointB) => pointA.timestamp - pointB.timestamp,
    );
  }, [historicalTimeSeries, timeSeries]);

  return { ...query, data, timeSeries, isFetchingHistorical, hasMoreHistoricalData };
};
